import { create } from 'zustand';

import { clearSpeakerTimer, startSpeakerTimer } from '@src/helpers';

type TSpeaker = {
  peer_id: string;
  peer_name: string;
  rawText: string;
  isFinal: boolean;
  isTyping: boolean;
};

type TClosedCaptionStore = {
  speakers: TSpeaker[];
  selectedLanguage: string;
  finalSpeakersText: Map<string, string[] | null>;
  nonFinalSpeakersText: Map<string, string[] | null>;
  addOrUpdateSpeaker: (data: { peer_id: string; peer_name: string; text: string; isFinal: boolean }) => void;
  resetSpeakerStates: ({ peerId }: { peerId: string }) => void;
  resetStates: () => void;
  closedCaptionsError: string | null;
  setClosedCaptionsError: (err: string | null) => void;
};

const defaultState = {
  speakers: [],
  selectedLanguage: 'en',
  finalSpeakersText: new Map<string, null>(),
  nonFinalSpeakersText: new Map<string, null>(),
  closedCaptionsError: null,
};

export const useClosedCaptionsStore = create<TClosedCaptionStore>((set, get) => ({
  ...defaultState,
  setClosedCaptionsError: (err) => set({ closedCaptionsError: err }),
  addOrUpdateSpeaker: ({ peer_id, peer_name, text, isFinal }) => {
    if (typeof text !== 'string') return;
    set((state) => {
      let speakers = [...state.speakers];
      let speakerIdx = speakers.findIndex((s) => s.peer_id === peer_id);
      let speaker = speakers[speakerIdx];
      const updatedFinalSpeakersText = new Map(state.finalSpeakersText);
      const updatedNonFinalSpeakersText = new Map(state.nonFinalSpeakersText);

      const finalText = state.finalSpeakersText.get(peer_id) || [];
      const nonFinalText = state.nonFinalSpeakersText.get(peer_id) || [];
      const speakerFinalText = [...finalText, text].filter((t) => !!t.trim());
      const speakerNonFinalText = [...nonFinalText, text].filter((t) => !!t.trim());
      if (isFinal) {
        updatedFinalSpeakersText.set(peer_id, speakerFinalText);
        updatedNonFinalSpeakersText.delete(peer_id);
      } else {
        // If not final, add to non-final text
        updatedNonFinalSpeakersText.set(peer_id, speakerNonFinalText);
      }

      if (!speaker) {
        // Add new speaker, remove oldest if > 2 speakers
        if (speakers.length >= 2) {
          const removedSpeaker = speakers.shift();
          if (removedSpeaker) {
            updatedFinalSpeakersText.delete(removedSpeaker.peer_id);
            updatedNonFinalSpeakersText.delete(removedSpeaker.peer_id);
          }
        }
        speaker = { peer_id, peer_name, rawText: '', isFinal, isTyping: true };
        speakers.push(speaker);
      }

      // Append new text
      speaker.rawText = isFinal ? speakerFinalText.join(' ') : `${speaker.rawText} ${text}`.trim();
      speaker.isFinal = isFinal;
      speaker.isTyping = !isFinal;

      // Start/reset timer for this speaker
      startSpeakerTimer(peer_id, (id) => {
        // Use the store's resetSpeakerStates
        get().resetSpeakerStates({ peerId: id });
      });

      return {
        speakers,
        finalSpeakersText: updatedFinalSpeakersText,
        nonFinalSpeakersText: updatedNonFinalSpeakersText,
      };
    });
  },
  resetSpeakerStates: ({ peerId }) => {
    // Clear timer when resetting
    clearSpeakerTimer(peerId);
    set((state) => {
      const updatedSpeakers = state.speakers.filter((speaker) => speaker.peer_id !== peerId);
      const updatedFinalSpeakersText = new Map(state.finalSpeakersText);
      const updatedNonFinalSpeakersText = new Map(state.nonFinalSpeakersText);

      updatedFinalSpeakersText.delete(peerId);
      updatedNonFinalSpeakersText.delete(peerId);

      return {
        speakers: updatedSpeakers,
        finalSpeakersText: updatedFinalSpeakersText,
        nonFinalSpeakersText: updatedNonFinalSpeakersText,
      };
    });
  },
  resetStates: () =>
    set(() => ({
      ...defaultState,
    })),
}));
